import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { seekAds } from '../utils/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';

const rateTypeLabel = { HOURLY: 'per hour', DAILY: 'per day', WEEKLY: 'per week', MONTHLY: 'per month', FIXED: 'fixed rate' };

export default function SeekAdDetail() {
  const { id }       = useParams();
  const { t, user, fetchBalance }  = useApp();
  const navigate     = useNavigate();
  const [ad,         setAd]         = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [contact,    setContact]    = useState(null);
  const [unlocking,  setUnlocking]  = useState(false);
  const [hireModal,  setHireModal]  = useState(false);
  const [hireMsg,    setHireMsg]    = useState('');
  const [hiring,     setHiring]     = useState(false);

  useEffect(() => {
    seekAds.get(id)
      .then(r => setAd(r))
      .catch(() => navigate('/find-hire'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleUnlock = async () => {
    if (!user) { navigate('/login'); return; }
    setUnlocking(true);
    try {
      const res = await seekAds.unlockContact(id);
      setContact({ email: res.seekerEmail, phone: res.seekerPhone });
      fetchBalance();
      toast.success(res.alreadyUnlocked ? 'Contact already unlocked' : 'Contact unlocked! (−2 tokens)');
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setUnlocking(false); }
  };

  const handleHire = async () => {
    if (!user) { navigate('/login'); return; }
    setHiring(true);
    try {
      await seekAds.sendHireRequest(id, { message: hireMsg });
      toast.success(t('hire_request_sent'));
      setHireModal(false);
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setHiring(false); }
  };

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;
  if (!ad)     return null;

  const isOwn      = user?.id === ad.seekerId;
  const canHire    = user?.role === 'POSTER' && ad.status === 'ACTIVE' && !ad.isHired && !isOwn;
  const canContact = user?.role === 'POSTER' && !isOwn;
  const daysAgo    = Math.floor((Date.now() - new Date(ad.createdAt)) / 86400000);

  return (
    <div className="container" style={{ paddingTop: '36px', paddingBottom: '60px', maxWidth: '900px' }}>
      <Link to="/find-hire" style={{ fontSize: '13px', color: 'var(--text3)', display: 'inline-flex', alignItems: 'center', gap: '4px', marginBottom: '20px', textDecoration: 'none' }}>
        ← Back to find a hire
      </Link>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', alignItems: 'start' }}>
        {/* Main */}
        <div>
          <div className="card" style={{ marginBottom: '14px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--brand-light)', color: 'var(--brand-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 700, flexShrink: 0 }}>
                {ad.seekerName?.[0]?.toUpperCase()}
              </div>
              <div>
                <h1 style={{ fontSize: '20px', fontWeight: 700, lineHeight: 1.2 }}>{ad.title}</h1>
                <div style={{ fontSize: '14px', color: 'var(--text2)', marginTop: '3px' }}>{ad.seekerName}</div>
                <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '2px' }}>
                  📍 {ad.location} · Posted {daysAgo === 0 ? 'today' : `${daysAgo}d ago`}
                </div>
              </div>
            </div>

            {/* Chips */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <span className="chip chip-teal">{ad.category}</span>
              {ad.status === 'HIRED'  && <span className="chip chip-blue">Hired</span>}
              {ad.status === 'ACTIVE' && <span className="chip chip-green">Available</span>}
              {ad.status === 'PAUSED' && <span className="chip chip-amber">Paused</span>}
            </div>

            {/* Bio */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>About</h2>
              <p style={{ fontSize: '15px', lineHeight: 1.75, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{ad.bio}</p>
            </div>

            {/* Skills */}
            {ad.skills?.length > 0 && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px', marginBottom: '14px' }}>
                <h2 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>{t('skills')}</h2>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {ad.skills.map(s => <span key={s} className="chip chip-teal" style={{ fontSize: '12px' }}>{s}</span>)}
                </div>
              </div>
            )}

            {/* Extra info */}
            {(ad.experience || ad.availability || ad.portfolioUrl) && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px' }}>
                {ad.experience   && <div style={{ fontSize: '13px', marginBottom: '6px' }}>💼 <strong>{t('experience')}:</strong> {ad.experience}</div>}
                {ad.availability && <div style={{ fontSize: '13px', marginBottom: '6px' }}>🕐 <strong>{t('availability')}:</strong> {ad.availability}</div>}
                {ad.portfolioUrl && (
                  <div style={{ fontSize: '13px' }}>
                    🔗 <strong>{t('portfolio')}:</strong>{' '}
                    <a href={ad.portfolioUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)' }}>{ad.portfolioUrl}</a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CTA for guests */}
          {!user && (
            <div className="card" style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>Want to hire {ad.seekerName}?</div>
              <div style={{ fontSize: '13px', color: 'var(--text3)', marginBottom: '14px' }}>Create a poster account to send hire requests.</div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <Link to="/register" className="btn btn-primary">{t('nav_register')}</Link>
                <Link to="/login" className="btn btn-outline">{t('login')}</Link>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Rate card */}
          <div className="card" style={{ marginBottom: '12px', background: 'var(--brand)', border: 'none' }}>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>Seek rate</div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
              ৳{Number(ad.rate).toLocaleString()}
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginTop: '4px' }}>
              {rateTypeLabel[ad.rateType] ?? ad.rateType}
            </div>
            {canHire && (
              <button className="btn btn-full" style={{ marginTop: '14px', background: 'rgba(255,255,255,0.2)', color: '#fff', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.3)' }}
                onClick={() => setHireModal(true)}>
                {t('send_hire_request')}
              </button>
            )}
            {ad.isHired && (
              <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                ✅ Already hired
              </div>
            )}
          </div>

          {/* Contact */}
          {canContact && (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Contact</div>
              {contact || ad.seekerEmail ? (
                <div>
                  <div style={{ fontSize: '13px', marginBottom: '6px' }}>📧 {contact?.email || ad.seekerEmail}</div>
                  <div style={{ fontSize: '13px' }}>📱 {contact?.phone || ad.seekerPhone}</div>
                </div>
              ) : (
                <button className="btn btn-outline btn-full btn-sm" onClick={handleUnlock} disabled={unlocking}>
                  {unlocking ? <span className="spinner" style={{ width: 14, height: 14 }} /> : `🔒 ${t('view_contact')} (−2 tokens)`}
                </button>
              )}
            </div>
          )}

          {/* Meta */}
          <div className="card" style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Details</div>
            {[
              ['Category', ad.category],
              ['Rate type', ad.rateType],
              ['Location', ad.location],
              ['Status', ad.isHired ? 'Hired' : ad.status],
              ['Published', new Date(ad.createdAt).toLocaleDateString('en-BD', { dateStyle: 'medium' })],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
                <span style={{ color: 'var(--text3)' }}>{k}</span>
                <span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Own ad controls */}
          {isOwn && (
            <div className="card">
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Manage</div>
              <Link to="/dashboard?tab=seek" className="btn btn-outline btn-full btn-sm">← My seek ads</Link>
            </div>
          )}
        </div>
      </div>

      {/* Hire modal */}
      <Modal open={hireModal} onClose={() => setHireModal(false)} title={`Send hire request to ${ad.seekerName}`}>
        <div className="form-group">
          <label className="form-label">Message (optional)</label>
          <textarea className="form-input" rows={4} value={hireMsg}
            onChange={e => setHireMsg(e.target.value)}
            placeholder="Describe the work, expectations, start date..." style={{ resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-outline" onClick={() => setHireModal(false)}>Cancel</button>
          <button className="btn btn-primary btn-full" onClick={handleHire} disabled={hiring}>
            {hiring ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Send request'}
          </button>
        </div>
      </Modal>

      <style>{`@media(max-width:700px){.container>div[style*="grid-template-columns"]{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
}
